import rules from "@/utils/inputRules";
import {isFalse, isTrue} from "@/utils/service/DocumentGeneration/answers";
import { translation } from "@/utils/service/Translations/translations";
import {TERMS_OF_SERVICE} from "@/utils/service/documents";

export default [
    {
        name: "General Information",
        inputs: [
            {
                model: "company.name",
                type: "businessName",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "company.email",
                type: "email",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "website_url",
                type: "websiteUrl",
                classes: "col-md-6",
                rules: [rules.required, rules.url],
            },
            {
                model: "company.address",
                type: "address",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                label: "Phone number for the refunds related questions",
                model: "company.phone",
                type: "phone",
                input: {
                    global: false,
                },
                rules: [rules.required],
            },
        ],
        actions: [
            {
                component: "v-btn",
                params: (self) => ({
                    color: "secondaryDark",
                    text: true,
                    class: "th-mr-12",
                    to: {
                        name: "project_index",
                        params: self?.$route.params,
                    },
                }),
                cta: "Edit project information",
            },
        ]
    },
    {
        name: "Terms of Services",
        inputs: [
            {
                title: "Do you have Terms of Service (also known as Terms and Conditions and Terms of Use) on your website?",
                model: "has_terms_of_services",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["has_terms_of_services"]),
                title: "Please insert the link to your Terms of Service",
                model: "has_terms_of_services_link",
                type: "projectDocument",
                policyType: TERMS_OF_SERVICE,
            },
            {
                condition: (model) => isFalse(model["has_terms_of_services"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title: `We strongly recommend that you include a Terms of Service on your website`,
                },
            },
            {
                condition: (model) => isFalse(model["has_terms_of_services"]),
                model: "tos_no_use_create",
                type: "projectDocumentCreateSuggest",
                policyType: TERMS_OF_SERVICE,
            },
        ],
    },
    {
        name: "Eligibility",
        inputs: [
            {
                title: "Are your all goods eligible for refund?",
                tooltip: "If you have discounted or other types of goods which are not eligible for refund, select \"No\"",
                model: "all_goods_refundable",
                type: "radio",
                default: "true",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => model["all_goods_refundable"] === "false",
                model: "refundable_goods_type",
                type: "radio",
                default: "defective",
                values: {
                    defective: "Only defective",
                    some: "Only some (you have the exceptions)",
                },
            },
            {
                condition: (model) =>
                    model["all_goods_refundable"] === "false" &&
                    model["refundable_goods_type"] === "some",
                model: "non_refundable_goods",
                type: "multiSelect",
                label: "Please select all exceptions applicable to you",
                default: [],
                values: [
                    "Goods that are not suitable for return because of sanitary and health reasons.",
                    "Goods that have been personalized for your buyer.",
                    "Goods that are likely to degrade or pass their expiration dates quickly, such as food.",
                    "Goods that are inseparably mixed with other goods after delivery.",
                    "Goods that have been unpacked after delivery, such as packaged video or audio recordings or packaged software that have been unpacked after delivery.",
                    "Newspapers, periodicals, or magazines.",
                    "Discounted items can not be returned.",
                ],
            },
            {
                condition: (model) =>
                    model["all_goods_refundable"] === "false" &&
                    model["refundable_goods_type"] === "some",
                model: "non_refundable_goods_other",
                type: "addOption",
                label: "Other",
                addLabel: "Enter other types of non-refundable goods",
                inline: true,
                default: [],
                options: [],
            },
        ],
        validate: (model) => {
            if (
                model["all_goods_refundable"] === "false" &&
                model["refundable_goods_type"] === "some"
            ) {
                return model["non_refundable_goods"].concat(
                    model["non_refundable_goods_other"]
                ).length > 0
                    ? ""
                    : translation.t(
                          "At least one has to be selected or entered"
                      );
            }

            return null;
        },
    },
    {
        name: "Term for refund",
        inputs: [
            {
                title: "How long does a buyer have to request a refund?",
                model: "refund_period",
                type: "radio",
                default: "14",
                values: {
                    14: "14 days",
                    30: "30 days",
                    other: "Other",
                },
            },
            {
                condition: (model) => model["refund_period"] === "other",
                model: "refund_period_other",
                type: "text",
                inputType: "number",
                rules: [rules.required, rules.number],
            },
        ],
    },
    {
        name: "Ways to ask for refund",
        inputs: [
            {
                title: "How does a buyer have to request a refund?",
                model: "refund_process",
                type: "multiSelect",
                result: "object",
                values: {
                    contact_support: "Buyer must contact your Support team",
                    submit_return_form:
                        "Buyer must send back the Return form received with the goods",
                    return_to_address:
                        "Buyer must send back the good to the address provided in Refund Policy",
                },
            },
            {
                condition: (model) =>
                    model["refund_process"]?.return_to_address === true,
                model: "refund_process_return_address",
                type: "custom",
                component: () =>
                    import(
                        "@/components/Documents/RefundPolicy/RefundPolicyReturnAddress.vue"
                    ),
            },
            {
                model: "refund_process_other",
                type: "addOption",
                label: "Other",
                addLabel: "Enter other types for requesting refund",
                inline: true,
                default: [],
                options: [],
            },
        ],
        validate: (model) => {
            return Object.values(model["refund_process"]).filter(v => v).concat(
                model["refund_process_other"]
            ).length > 0
                ? ""
                : translation.t("At least one has to be selected or entered");
        },
    },
    {
        name: "Shipping costs",
        inputs: [
            {
                title:
                    "Who will be responsible for paying the return shipping costs?",
                model: "return_cost_covered_by_company",
                type: "radio",
                default: "false",
                values: {
                    false: "Buyer",
                    true: "You",
                },
            },
        ],
    },
    {
        name: "Money back term",
        inputs: [
            {
                title:
                    "How long does a buyer have to wait for a refund after the goods are returned?",
                model: "refund_issue_period",
                type: "radio",
                default: "14",
                values: {
                    14: "14 days",
                    30: "30 days",
                    other: "Other",
                },
            },
            {
                condition: (model) => model["refund_issue_period"] === "other",
                model: "refund_issue_period_other",
                type: "text",
                inputType: "number",
                rules: [rules.required, rules.number],
            },
        ],
    },
    {
        name: "Defective goods",
        inputs: [
            {
                title:
                    "What information your buyer should provide to you in case he wants to return the defected goods?",
                model: "required_defective_goods_info",
                type: "multiSelect",
                default: ["Proof of purchase"],
                values: [
                    "Proof of purchase",
                    "Order number",
                    "VAT invoice number",
                    "Product name",
                    "Information about the defect, failure or missing part of the item",
                    "Defect evidence (e.g. a photo or video of the item, a photo of the defective area (if it is possible to take a photo), a photo of the packaging, other).",
                ],
            },
            {
                model: "required_defective_goods_info_other",
                type: "addOption",
                label: "Other",
                addLabel: "Enter other required information",
                inline: true,
                default: [],
                options: [],
            },
        ],
        validate: (model) => {
            return model["required_defective_goods_info"].concat(
                model["required_defective_goods_info_other"]
            ).length > 0
                ? ""
                : translation.t("At least one has to be selected or entered");
        },
    },
    {
        name: "Complaint resolution",
        inputs: [
            {
                title: "What ways you can offer to resolve the claim? (Please select all that apply)",
                model: "complaint_resolution_methods",
                type: "multiSelect",
                validate: (model) =>
                    model.length > 0
                        ? ""
                        : translation.t("At least one has to be selected"),
                default: [],
                values: [
                    "To replace the defective goods with quality items",
                    "To supplement the incomplete goods with missing components",
                    "To apply a discount to the goods, e.g. reduce the price of the goods accordingly",
                    "To refund the money paid",
                    "To receive store credit",
                ],
            },
        ],
    },
];
